import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { COViewMapCard } from "./ChangeOrder/COViewMapCard";
import { COStatusActions } from "./ChangeOrder/COStatusActions";
import { groupLineItemsBySections, hasSections } from "../utils/lineItemSections";
import { FormattedDescription } from "../components/FormattedDescription";

const ChangeOrderViewPage = () => {
  const { changeOrderId } = useParams();
  const [changeOrder, setChangeOrder] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchChangeOrder = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/change-orders/${changeOrderId}`);
      setChangeOrder(response.data);
      if (response.data.quote_id) {
        const quoteResponse = await axios.get(`${API}/quotes/${response.data.quote_id}`);
        setQuote(quoteResponse.data);
      }
    } catch {
      toast.error("Change order not found");
    } finally {
      setLoading(false);
    }
  }, [changeOrderId]);

  useEffect(() => {
    fetchChangeOrder();
  }, [fetchChangeOrder]);

  if (loading) return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00341d]" />
    </div>
  );

  if (!changeOrder) return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-zinc-900">Change Order Not Found</h1>
        <p className="text-zinc-600 mt-2">This change order may have been removed or the link is invalid.</p>
      </div>
    </div>
  );

  const isApproved = changeOrder.status === "approved" || changeOrder.status === "paid";
  const isRejected = changeOrder.status === "rejected";
  const isPaid = changeOrder.payment_status === "paid";
  const needsApproval = changeOrder.requires_approval && changeOrder.status === "sent";
  const canPay = (isApproved || !changeOrder.requires_approval) && !isPaid && changeOrder.subtotal > 0;
  const totalWithFee = Math.floor(changeOrder.subtotal * 1.03 * 100) / 100;

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="bg-[#00341d] py-6">
        <div className="max-w-3xl mx-auto px-4">
          <img
            src="https://customer-assets.emergentagent.com/job_06366a4e-f06e-49f6-ad8a-6605b3e3cdc0/artifacts/h5dfnsjk_Logo_text_green-transparent%20background.png"
            alt="Land ClearCo"
            className="h-12 brightness-0 invert mx-auto"
          />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="bg-white border border-zinc-200 shadow-sm">
          <CardHeader className="border-b border-zinc-100 bg-zinc-50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-bold text-[#00341d]">
                  Change Order {changeOrder.change_order_number}
                </CardTitle>
                <p className="text-sm text-zinc-500 mt-1">For Quote: {changeOrder.quote_number}</p>
              </div>
              <div className="text-right">
                {isPaid && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-4 h-4 mr-1" /> Paid
                  </span>
                )}
                {isApproved && !isPaid && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <CheckCircle className="w-4 h-4 mr-1" /> Approved
                  </span>
                )}
                {isRejected && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    <XCircle className="w-4 h-4 mr-1" /> Rejected
                  </span>
                )}
                {needsApproval && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                    Awaiting Approval
                  </span>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-zinc-500">Customer</p>
                <p className="font-medium">{changeOrder.customer_name}</p>
              </div>
              {quote?.property_address && (
                <div>
                  <p className="text-zinc-500">Property</p>
                  <p className="font-medium">{quote.property_address}</p>
                </div>
              )}
              {changeOrder.invoice_number && (
                <div>
                  <p className="text-zinc-500">Linked Invoice</p>
                  <p className="font-medium">{changeOrder.invoice_number}</p>
                </div>
              )}
            </div>

            {/* Map Areas — extracted component */}
            {changeOrder.map_areas?.length > 0 && (
              <COViewMapCard changeOrder={changeOrder} quote={quote} />
            )}

            {/* Line Items */}
            {changeOrder.line_items?.length > 0 && (
              <div>
                <h3 className="font-semibold text-zinc-900 mb-3">Additional Services</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-medium text-zinc-600">Service</th>
                        <th className="text-right px-4 py-2 text-sm font-medium text-zinc-600">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupLineItemsBySections(changeOrder.line_items).map((group, gIdx) => (
                        <COGroupRows
                          key={group.header?.id || `co-view-ug-${gIdx}`}
                          group={group}
                          showSubtotal={hasSections(changeOrder.line_items)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Adjustment */}
            {changeOrder.adjustment_amount !== 0 && (
              <div className={`p-4 rounded-lg ${changeOrder.adjustment_amount < 0 ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
                <div className="flex justify-between items-center">
                  <p className={`font-medium ${changeOrder.adjustment_amount < 0 ? "text-green-800" : "text-amber-800"}`}>
                    {changeOrder.adjustment_description || (changeOrder.adjustment_amount < 0 ? "Credit" : "Additional Charge")}
                  </p>
                  <p className={`font-bold text-lg ${changeOrder.adjustment_amount < 0 ? "text-green-700" : "text-amber-700"}`}>
                    {changeOrder.adjustment_amount < 0 ? "-" : "+"}${Math.abs(changeOrder.adjustment_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            )}

            {/* Notes */}
            {changeOrder.notes && (
              <div className="bg-zinc-50 rounded-lg p-4">
                <p className="text-sm font-medium text-zinc-600 mb-1">Notes</p>
                <p className="text-zinc-800">{changeOrder.notes}</p>
              </div>
            )}

            {/* Total */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center text-lg">
                <span className="font-semibold text-zinc-900">Change Order Total</span>
                <span className="font-bold text-[#00341d]">
                  ${changeOrder.subtotal?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
              {canPay && (
                <div className="flex justify-between items-center text-sm text-zinc-500 mt-1">
                  <span>Including 3% CC processing fee</span>
                  <span>${totalWithFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>

            {/* Approval / Payment actions — extracted component */}
            <COStatusActions changeOrder={changeOrder} onRefresh={fetchChangeOrder} />
          </CardContent>
        </Card>

        <div className="text-center mt-8 text-sm text-zinc-500">
          <p>Land ClearCo, LLC | Professional Land Clearing Services</p>
        </div>
      </div>
    </div>
  );
};

const COGroupRows = ({ group, showSubtotal }) => {
  const { header, items, subtotal } = group;
  return (
    <>
      {header && (
        <tr className="bg-[#00341d]/5 border-t" data-testid={`co-view-section-${header.id}`}>
          <td colSpan={2} className="px-4 py-2">
            <p className="font-heading uppercase tracking-wider text-[#00341d] text-xs font-bold">
              {header.service_name || "Section"}
            </p>
          </td>
        </tr>
      )}
      {items.map((item, idx) => (
        <tr key={item.id || idx} className="border-t">
          <td className="px-4 py-3">
            <p className="font-medium text-zinc-900">{item.service_name}</p>
            {item.description && <FormattedDescription text={item.description} className="text-sm text-zinc-500" />}
            {item.quantity > 0 && item.unit_price > 0 && (
              <p className="text-xs text-zinc-400">{item.quantity} × ${item.unit_price.toLocaleString()}</p>
            )}
          </td>
          <td className="px-4 py-3 text-right font-medium">
            ${item.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </td>
        </tr>
      ))}
      {header && showSubtotal && items.length > 0 && (
        <tr className="border-t bg-zinc-50/60" data-testid={`co-view-section-subtotal-${header.id}`}>
          <td className="px-4 py-2 text-right text-sm text-zinc-600">Section subtotal</td>
          <td className="px-4 py-2 text-right text-sm font-semibold text-[#00341d]">
            ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </td>
        </tr>
      )}
    </>
  );
};

export default ChangeOrderViewPage;
